import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { CommunityLeaderboardService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the query/mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching the community leaderboard list.
 *
 * @param {Object} params - Query parameters (scope, page, limit)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useCommunityLeaderboardList = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["community-leaderboard-list", params],
    queryFn: () => {
      return CommunityLeaderboardService.getList(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

export { useCommunityLeaderboardList };
